/**
 * AITextInput Component
 *
 * Text input with an AI suggestion button underneath. Clicking the button asks
 * the backend game mode for suggested values; the returned words are rendered
 * as buttons that fill the input when clicked.
 *
 * Reads session/player/request identifiers straight from Zustand stores — the
 * parent form does not need to drill them down.
 */

import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import type { AITextInputFieldDef } from "@/types/actions";
import { submitAISuggest } from "@/services/api";
import { usePlayerStore } from "@/stores/playerStore";
import { useGameStore } from "@/stores/gameStore";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

interface AITextInputProps {
  field: AITextInputFieldDef;
  value: string;
  onChange: (value: string) => void;
  disabled: boolean;
  /** Notifies the parent form while a suggestion request is in flight. */
  onLoadingChange?: (loading: boolean) => void;
}

export const AITextInput: React.FC<AITextInputProps> = ({
  field,
  value,
  onChange,
  disabled,
  onLoadingChange,
}) => {
  const { t } = useTranslation();
  const sessionCode = usePlayerStore((s) => s.sessionCode);
  const playerId = usePlayerStore((s) => s.playerId);
  const requestId = useGameStore((s) => s.currentQuestion?.requestId ?? null);

  const [loading, setLoading] = useState(false);
  const [suggestions, setSuggestions] = useState<string[] | null>(null);
  // Sticky flag: the allowance is spent either locally (this component issued a
  // request) or server-side (`field.suggest_disabled`). Derived rather than synced
  // via an effect, so the panel stays correct even without a remount.
  const [usedLocally, setUsedLocally] = useState(false);
  const used = field.suggest_disabled === true || usedLocally;
  // Set once the player takes one of the suggestions — the row is then removed
  // entirely rather than falling back to a permanently greyed-out button.
  const [picked, setPicked] = useState(false);
  // Synchronous guard: two clicks in the same tick both pass a state-based check,
  // because React has not re-rendered the disabled attribute yet.
  const inFlight = useRef(false);

  const canSuggest =
    !disabled &&
    !loading &&
    !used &&
    sessionCode !== null &&
    playerId !== null &&
    requestId !== null;

  const setLoadingState = (next: boolean) => {
    setLoading(next);
    onLoadingChange?.(next);
  };

  const handleSuggest = async () => {
    if (
      !canSuggest ||
      sessionCode === null ||
      playerId === null ||
      requestId === null
    )
      return;
    if (inFlight.current) return;
    inFlight.current = true;

    setLoadingState(true);
    try {
      const result = await submitAISuggest(sessionCode, {
        player_id: playerId,
        request_id: requestId,
        field_id: field.id,
      });

      if (result.error) {
        toast.error(result.error);
        return;
      }

      if (result.suggestions.length === 0) {
        toast.error(t("form.aiSuggestEmpty"));
        return;
      }

      setSuggestions(result.suggestions);
      setUsedLocally(true);
    } catch {
      toast.error(t("form.aiSuggestFailed"));
    } finally {
      inFlight.current = false;
      setLoadingState(false);
    }
  };

  const handlePick = (suggestion: string) => {
    onChange(suggestion);
    setPicked(true);
  };

  return (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor={field.id} className="text-sm font-semibold">
        {field.label}
      </Label>
      {field.description && (
        <p className="text-xs text-muted-foreground">{field.description}</p>
      )}
      <Input
        id={field.id}
        placeholder={field.placeholder}
        maxLength={field.max_length}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        className="min-h-[44px]"
      />
      {picked ? null : (
        <div
          className="flex flex-wrap gap-1.5"
          data-testid={`ai-suggest-${field.id}`}
        >
          {suggestions === null ? (
            <button
              type="button"
              onClick={handleSuggest}
              disabled={!canSuggest}
              data-testid={`ai-suggest-button-${field.id}`}
              className="px-3 py-1.5 rounded-full text-xs font-medium border border-border/40 bg-card/50 text-foreground transition-colors hover:border-primary/40 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading
                ? field.suggest_loading_label
                : field.suggest_button_label}
            </button>
          ) : (
            suggestions.map((suggestion) => (
              <button
                key={suggestion}
                type="button"
                onClick={() => handlePick(suggestion)}
                disabled={disabled}
                data-testid={`ai-suggestion-${suggestion}`}
                className="px-3 py-1.5 rounded-full text-xs font-medium border border-primary/40 bg-primary/10 text-foreground transition-colors hover:bg-primary/20 disabled:opacity-50"
              >
                {suggestion}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
};
